import type { DevedorTop, FaturaPainel } from './types.ts';
import { chaveCliente, valorDevido, STATUS_SEM_COBRANCA } from '../statusBoleto.ts';

// Fonte única de "quais faturas formam cada indicador" do Painel Financeiro
// Executivo. O card soma, a lista de boletos mostra e o Excel exporta a partir
// da MESMA função — assim o total da lista é igual ao do card por construção.
//
// Antes cada bloco da tela tinha o seu filtro e a sua soma: os KPIs liam
// `valorDevido` e os gráficos/Top 10/donut liam `valor_boleto_vigor` cru, e as
// faturas de boleto único (valor em `valor_emissao_boleto_unico`) entravam com
// R$ 0 em metade da tela. O valor de cada fatura é sempre `valorDevido`.
//
// Função pura, sem I/O nem JSX: roda sob `node --test` com type-stripping.

export type IdKpi =
  | 'emitido' | 'recebido' | 'aVencer' | 'vencido'
  | 'taxaInadimplencia' | 'ticketMedio' | 'prazoMedio' | 'recuperado';

export function paraDataLocalSemHora(valor: string): Date {
  const [ano, mes, dia] = valor.split('T')[0].split('-').map(Number);
  return new Date(ano, (mes || 1) - 1, dia || 1);
}

function diffEmDias(de: Date, para: Date): number {
  return Math.round((para.getTime() - de.getTime()) / 86400000);
}

/**
 * Conta da própria Vigor, não de cliente.
 *
 * A ASSOCIACAO VIGOR ENERGY GO aparecia como 2º MAIOR DEVEDOR do painel:
 * R$ 89.737,49 em 36 boletos vencidos, 29 UCs, de MAR a SET/2026 — quase 10%
 * de toda a inadimplência (auditoria de 21/09/2026, solicitação 5955).
 *
 * Dívida da casa com a casa não é inadimplência de cliente: inflava o vencido,
 * a taxa, o aging e o Top Devedores, e empurrava um devedor real para fora do
 * Top 10.
 *
 * ⚠️ Compara SEM acento e por prefixo porque o nome chega do cadastro com
 * grafias diferentes ("ASSOCIACAO VIGOR ENERGY GO", "Associação Vigor Energy").
 * Nenhum cliente real se chama assim — é a razão social da própria associação.
 */
export function ehContaInterna(f: FaturaPainel): boolean {
    const nome = (f.cliente_nome || '')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .trim().toUpperCase();
    return nome.startsWith('ASSOCIACAO VIGOR');
}

/**
 * Tira do painel o que não é cobrança a cliente. Aplicado na ENTRADA de todos
 * os cálculos, e não dentro de `ehCobranca`, porque vencido/aging/Top Devedores
 * filtram por `statusDerivado` e não passariam por aquele gate.
 */
export const faturasDoPainel = (dados: FaturaPainel[]): FaturaPainel[] => dados.filter((f) => !ehContaInterna(f));

/**
 * Fatura que virou cobrança — o universo de "emitido".
 *
 * Fora dele, e cada um por um motivo diferente:
 *
 *   - 'Sem cobrança' / 'Sem injeção': nunca chegaram a ser cobrança.
 *
 *   - 'Cancelado' (R$ 88.087,80 em 65 boletos, 1,44%): a cobrança foi baixada e
 *     NÃO voltou — cancelar-para-reemitir não cai aqui, porque nesse caso a
 *     fatura fica com o boleto novo. Até 21/09/2026 contava como emitido ("foi
 *     emitido e depois baixado"), o que diluía o denominador e fazia a taxa de
 *     inadimplência parecer melhor do que é (solicitação 5957).
 *
 *   - 'Quitado' (R$ 24.497,81): foi pago por OUTRA cobrança — um avulso ou
 *     unificado, que é uma fatura como qualquer outra e já está neste universo.
 *     Contar os dois põe a mesma dívida duas vezes no emitido. O 'recebido' já
 *     excluía o quitado desde 17/09/2026 exatamente por isso; o 'emitido' tinha
 *     ficado de fora da mesma correção (solicitação 5956).
 *
 * Efeito medido em 21/09/2026: emitido de R$ 6.134.893,41 para R$ 6.022.307,80
 * (−1,8%) e taxa de inadimplência de 14,97% para 15,25%. A taxa não piorou —
 * parou de ser diluída por cobrança que não existe mais.
 */
export const ehCobranca = (f: FaturaPainel): boolean =>
    !STATUS_SEM_COBRANCA.includes(f.statusDerivado)
    && f.statusDerivado !== 'Cancelado'
    && f.statusDerivado !== 'Quitado';

/**
 * Dinheiro que entrou por ESTA fatura.
 *
 * ⚠️ 'Quitado' NÃO entra, e isso é o oposto do que valia até 17/09/2026.
 *
 * Quitado quer dizer "o cliente pagou isto em outra cobrança" — um boleto
 * avulso/unificado que junta várias faturas. Esse avulso é uma fatura como
 * qualquer outra e, quando pago, já entra aqui como 'Pago'. Somar os dois conta
 * o mesmo dinheiro duas vezes: eram R$ 24.336,01 em 67 faturas quitadas entre
 * junho e setembro de 2026, todas com um avulso pago correspondente.
 *
 * O financeiro já sabia disso e se defendia na mão — a Meryene tirava os
 * quitados do relatório antes de fechar o caixa, justamente para não duplicar.
 * A regra agora é a mesma dela.
 *
 * Quitado continua contando como cobrança emitida (`ehCobranca`) e continua
 * fora da inadimplência: não é dívida, só não é dinheiro novo.
 */
export const estaPago = (f: FaturaPainel): boolean => f.statusDerivado === 'Pago';

/** Paga por outra cobrança (avulso/unificada). Fora do caixa, fora da dívida. */
export const estaQuitado = (f: FaturaPainel): boolean => f.statusDerivado === 'Quitado';

export const dataVencimentoDe = (f: FaturaPainel): string | null => f.vencimento_boleto || f.data_vencimento || null;

/** Dias entre o vencimento e o pagamento (negativo = pagou adiantado). `null`
 *  quando a fatura não está paga ou falta uma das duas datas. */
export function diasPagamentoAposVencimento(f: FaturaPainel): number | null {
  const venc = dataVencimentoDe(f);
  if (!estaPago(f) || !f.data_pgto_boleto || !venc) return null;
  return diffEmDias(paraDataLocalSemHora(venc), paraDataLocalSemHora(f.data_pgto_boleto));
}

/**
 * Dias entre a EMISSÃO do boleto e o pagamento — o "prazo de recebimento" no
 * sentido que se espera dessa palavra: quanto tempo o dinheiro leva para entrar
 * depois que a cobrança saiu. Sempre positivo na prática.
 *
 * É diferente de `diasPagamentoAposVencimento`, que mede pontualidade e é
 * NEGATIVO quando o cliente paga adiantado — o que é o caso da maioria aqui
 * (medido em 16/09/2026: dos 307 boletos pagos vencendo em setembro, 182
 * pagaram antes do vencimento e 75 no dia).
 *
 * `null` quando falta a data de pagamento ou a de emissão. A emissão vem de
 * `boleto_emissao_data`, com `emitido_em` como reserva — 1,5% das faturas com
 * boleto não têm nenhuma das duas e ficam de fora da média.
 */
export function diasEmissaoAtePagamento(f: FaturaPainel): number | null {
  if (!estaPago(f) || !f.data_pgto_boleto) return null;
  const emissao = String(f.boleto_emissao_data || f.emitido_em || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(emissao)) return null;
  return diffEmDias(paraDataLocalSemHora(emissao), paraDataLocalSemHora(f.data_pgto_boleto));
}

/** Em aberto com vencimento entre hoje e hoje+7, inclusive. */
export function venceEmAte7Dias(f: FaturaPainel, hoje: Date): boolean {
  if (f.statusDerivado !== 'Em aberto') return false;
  const venc = dataVencimentoDe(f);
  if (!venc) return false;
  const hojeSemHora = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  const em7Dias = new Date(hojeSemHora);
  em7Dias.setDate(em7Dias.getDate() + 7);
  const d = paraDataLocalSemHora(venc).getTime();
  return d >= hojeSemHora.getTime() && d <= em7Dias.getTime();
}

export const somaValorDevido = (faturas: FaturaPainel[]): number => faturas.reduce((s, f) => s + valorDevido(f), 0);

export interface DefinicaoKpi {
  id: IdKpi;
  titulo: string; // igual ao título do card
  /** Diz em uma frase quais boletos a lista traz — cabeçalho da lista e do Excel. */
  descricaoLista: string;
  filtrar: (dados: FaturaPainel[]) => FaturaPainel[];
}

const cobrancas = (dados: FaturaPainel[]) => dados.filter(ehCobranca);
const vencidos = (dados: FaturaPainel[]) => dados.filter((f) => f.statusDerivado === 'Vencido');

export const DEFINICOES_KPI: Record<IdKpi, DefinicaoKpi> = {
  emitido: {
    id: 'emitido', titulo: 'Faturamento Emitido',
    descricaoLista: 'Boletos emitidos — todas as cobranças, fora "Sem cobrança" e "Sem injeção"',
    filtrar: cobrancas,
  },
  recebido: {
    id: 'recebido', titulo: 'Recebido',
    descricaoLista: 'Boletos pagos. Quitados ficam fora — o dinheiro deles entrou pelo boleto avulso/unificado, que já está aqui',
    filtrar: (dados) => dados.filter(estaPago),
  },
  aVencer: {
    id: 'aVencer', titulo: 'A Vencer',
    descricaoLista: 'Boletos em aberto, ainda dentro do vencimento',
    filtrar: (dados) => dados.filter((f) => f.statusDerivado === 'Em aberto'),
  },
  vencido: {
    id: 'vencido', titulo: 'Vencido (Inadimplência)',
    descricaoLista: 'Boletos vencidos e não pagos',
    filtrar: vencidos,
  },
  taxaInadimplencia: {
    id: 'taxaInadimplencia', titulo: 'Taxa de Inadimplência',
    descricaoLista: 'Boletos vencidos — o numerador da taxa (vencido ÷ emitido)',
    filtrar: vencidos,
  },
  ticketMedio: {
    id: 'ticketMedio', titulo: 'Ticket Médio',
    descricaoLista: 'Boletos emitidos — base do ticket médio (emitido ÷ quantidade)',
    filtrar: cobrancas,
  },
  prazoMedio: {
    id: 'prazoMedio', titulo: 'Prazo Médio de Recebimento',
    descricaoLista: 'Boletos pagos com data de vencimento e de pagamento — o card mostra a média da emissão ao pagamento; a coluna "Dias" conta a partir do vencimento (negativo = pagou adiantado)',
    filtrar: (dados) => dados.filter((f) => diasPagamentoAposVencimento(f) !== null),
  },
  recuperado: {
    id: 'recuperado', titulo: 'Recuperado',
    descricaoLista: 'Boletos pagos depois do vencimento',
    filtrar: (dados) => dados.filter((f) => (diasPagamentoAposVencimento(f) ?? 0) > 0),
  },
};

export function faturasDoKpi(id: IdKpi, dados: FaturaPainel[]): FaturaPainel[] {
  return DEFINICOES_KPI[id].filtrar(dados);
}

/** Maiores devedores (valor vencido por cliente), agrupados pela mesma chave da
 *  Central de Inadimplência. `limite` omitido devolve todos. */
export function calcularTopDevedores(dadosEntrada: FaturaPainel[], limite?: number): DevedorTop[] {
  // Conta interna fora do ranking: a Associação Vigor era o 2º maior devedor
  // e empurrava um devedor real para fora do Top 10 (solicitação 5955).
  const dados = faturasDoPainel(dadosEntrada);
  const acc = new Map<string, DevedorTop>();
  vencidos(dados).forEach((f) => {
    const chave = chaveCliente(f.cliente_nome);
    const atual = acc.get(chave) || { cliente: f.cliente_nome || 'Desconhecido', cpfCnpj: f.cliente_cpf_cnpj, qtdeBoletos: 0, valorVencido: 0 };
    atual.qtdeBoletos += 1;
    atual.valorVencido += valorDevido(f);
    if (!atual.cpfCnpj && f.cliente_cpf_cnpj) atual.cpfCnpj = f.cliente_cpf_cnpj;
    acc.set(chave, atual);
  });
  const lista = Array.from(acc.values()).sort((a, b) => b.valorVencido - a.valorVencido);
  return limite === undefined ? lista : lista.slice(0, limite);
}
